import { Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { Button, Modal } from 'react-bootstrap';

import TherigyErrorBoundary from 'App/components/errors/TherigyErrorBoundary';
import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import { toDate } from 'App/services/DateService';
import getFeatureFlagStatus from 'App/utils/getFeatureFlagStatus';
import { PERMISSION_TYPES } from 'App/utils/isPermitted';
import { CheckboxField } from 'Shared/form/CheckboxField';
import SubmitButton from 'Shared/form/SubmitButton';
import { patientTherapyPropType } from 'Shared/types';

import { patientMedicationRxPropType } from '../../types';
import SpaceBetweenContainer from '../SpaceBetweenContainer';
import EditMedicationModalSection from './EditMedicationModalSection';
import EditTherapeuticCategoryModalSection from './EditTherapeuticCategoryModalSection';

const isMeaningfulValue = (value) => {
    if (value == null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (value instanceof Date) return !Number.isNaN(value.getTime());
    if (Array.isArray(value)) return value.some(isMeaningfulValue);
    if (typeof value === 'object') return Object.values(value).some(isMeaningfulValue);
    return true;
};

const hasRxInput = (rx) => rx != null && Object.values(rx).some(isMeaningfulValue);

const EditTherapeuticCategoryMedicationModal = ({
    existingTherapy,
    existingMedication,
    editMode,
    onClose,
    onSubmit,
}) => {
    const {
        values: { id: patientId, rxs, therapeuticCategory, medications },
    } = useFormikContext();
    const isCodifiedMedicationsEnabled = getFeatureFlagStatus(PERMISSION_TYPES.ENABLE_CODIFY_MEDICATIONS);

    return (
        <Modal show size="lg" onHide={onClose} backdrop="static">
            <TherigyErrorBoundary>
                <Modal.Header>
                    <Modal.Title role="heading">Add Therapeutic Category & Medication</Modal.Title>
                </Modal.Header>
                <Formik
                    initialValues={{
                        patientId,
                        therapy: {
                            ...existingTherapy,
                            category: existingTherapy
                                ? {
                                      id: existingTherapy.id,
                                      name: existingTherapy.name,
                                  }
                                : null,
                            status: {
                                ...existingTherapy?.status,
                                optIn: existingTherapy?.status?.optIn ?? true,
                                start: toDate(existingTherapy?.status?.start),
                            },
                        },
                        patientMedication: {
                            ...existingMedication,
                            medication: existingMedication?.medication
                                ? {
                                      ...existingMedication.medication,
                                      ID: existingMedication.medication.id,
                                      Name: existingMedication.medication.name,
                                  }
                                : null,
                            status: {
                                ...existingMedication?.status,
                                start: toDate(existingMedication?.status.start),
                            },
                        },
                        rxs: (() => {
                            const filteredRxs = rxs
                                .filter((rx) => rx.medication.id === existingMedication?.medication.id)
                                .map((rx) => ({
                                    ...rx,
                                    start: toDate(rx.start),
                                    fillExpiration: toDate(rx.fillExpiration),
                                    rxFill: rx.rxFill?.map((rxFill) => ({
                                        ...rxFill,
                                        fillDate: toDate(rxFill.fillDate),
                                    })),
                                }));

                            if (isCodifiedMedicationsEnabled && filteredRxs.length === 0) {
                                return [{}];
                            }

                            return filteredRxs;
                        })(),
                        addMoreMedications: false,
                    }}
                    onSubmit={(values) => {
                        const submissionValues = {
                            ...values,
                            therapy: {
                                ...values.therapy,
                                id: values.therapy.category.id,
                                name: values.therapy.category.name,
                            },
                            patientMedication: {
                                ...values.patientMedication,
                                medication: values.patientMedication.medication
                                    ? {
                                          id: values.patientMedication.medication.ID,
                                          name: values.patientMedication.medication.Name,
                                          groupId: values.therapy.category.id,
                                          newMedication: !editMode && !existingMedication,
                                      }
                                    : null,
                                therapy: {
                                    name: values.therapy.category.name,
                                },
                            },
                            rxs: values.rxs.filter(hasRxInput).map((rx) => ({
                                ...rx,
                                medication: rx.medication || {
                                    id: values.patientMedication.medication.ID,
                                    name: values.patientMedication.medication.Name,
                                },
                            })),
                        };
                        onClose();
                        onSubmit(submissionValues);
                    }}
                >
                    {({ handleSubmit, values }) => (
                        <form onSubmit={handleSubmit}>
                            <EditTherapeuticCategoryModalSection
                                existingTherapy={existingTherapy}
                                allTherapies={therapeuticCategory}
                            />

                            {values.therapy.category?.id && !editMode && (
                                <EditMedicationModalSection
                                    existingMedication={existingMedication}
                                    allMedications={medications}
                                />
                            )}

                            <Modal.Footer>
                                <div className="w-100">
                                    <SpaceBetweenContainer>
                                        <div>
                                            {!editMode &&
                                                !existingTherapy?.status.id &&
                                                values.patientMedication.medication && (
                                                    <CheckboxField
                                                        fieldName="addMoreMedications"
                                                        label="Add more medications"
                                                    />
                                                )}
                                        </div>

                                        <FormButtonGroup>
                                            <Button variant="outline-secondary" onClick={onClose}>
                                                Cancel
                                            </Button>
                                            <SubmitButton ignoreValid>Add</SubmitButton>
                                        </FormButtonGroup>
                                    </SpaceBetweenContainer>
                                </div>
                            </Modal.Footer>
                        </form>
                    )}
                </Formik>
            </TherigyErrorBoundary>
        </Modal>
    );
};

EditTherapeuticCategoryMedicationModal.propTypes = {
    existingTherapy: patientTherapyPropType,
    existingMedication: patientMedicationRxPropType,
    editMode: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
};

EditTherapeuticCategoryMedicationModal.defaultProps = {
    existingTherapy: null,
    existingMedication: null,
};

export default EditTherapeuticCategoryMedicationModal;
